"use client";

import { ArrowRight } from "@phosphor-icons/react";
import { useTranslations } from "next-intl";

import { Link } from "@/navigation";

const FLOATING_BADGES = [
	{
		key: "readyToStartBadge1",
		className: "left-[4%] top-[28%] bg-[#dcecff] text-[#4f78ad] sm:left-[7%] lg:left-[8%]",
	},
	{
		key: "readyToStartBadge2",
		className: "left-[10%] top-[66%] bg-[#eee8ff] text-[#7a67c7] sm:left-[13%] lg:left-[12%]",
	},
	{
		key: "readyToStartBadge3",
		className: "right-[3%] top-[24%] bg-[#ffe4f1] text-[#c96a9e] sm:right-[6%] lg:right-[5%]",
	},
	{
		key: "readyToStartBadge4",
		className: "right-[4%] top-[66%] bg-[#dff8e6] text-[#56a76b] sm:right-[7%] lg:right-[6%]",
	},
] as const;

const PlaceholderLine = ({ className = "" }: { className?: string }) => (
	<span className={`block rounded-full bg-gray-200/70 ${className}`} aria-hidden="true" />
);

export default function IndustrialAutomationReadyToStart() {
	const t = useTranslations("new-industrial-automation");

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="relative mx-auto min-h-[230px] max-w-[1020px] overflow-hidden rounded-t-xl bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:min-h-[280px] lg:min-h-[330px]">
					<div className="flex h-8 items-center gap-1.5 bg-[#f3f3f3] px-4 sm:h-10">
						<span className="h-2 w-2 rounded-full bg-[#ff6b6b]" aria-hidden="true" />
						<span className="h-2 w-2 rounded-full bg-[#ffc75f]" aria-hidden="true" />
						<span className="h-2 w-2 rounded-full bg-[#57cc99]" aria-hidden="true" />
					</div>

					<div className="relative min-h-[198px] px-4 py-8 sm:min-h-[240px] sm:px-8 sm:py-10 lg:min-h-[290px] lg:px-12">
						<div
							className="pointer-events-none absolute inset-x-4 top-10 hidden justify-between opacity-50 sm:flex lg:inset-x-10"
							aria-hidden="true"
						>
							<div className="w-32 space-y-4 lg:w-44">
								<PlaceholderLine className="h-3 w-28" />
								<PlaceholderLine className="ml-12 h-3 w-20" />
							</div>
							<div className="flex gap-6">
								<PlaceholderLine className="h-3 w-16" />
								<PlaceholderLine className="h-3 w-16" />
								<PlaceholderLine className="h-3 w-16" />
							</div>
						</div>

						<div className="pointer-events-none absolute bottom-12 left-[7%] hidden w-28 space-y-3 opacity-60 sm:block">
							<PlaceholderLine className="h-3 w-20" />
							<PlaceholderLine className="h-3 w-full" />
							<PlaceholderLine className="h-3 w-24" />
							<PlaceholderLine className="h-3 w-16" />
							<PlaceholderLine className="h-3 w-24" />
							<PlaceholderLine className="h-3 w-20" />
						</div>

						<div className="pointer-events-none absolute bottom-12 right-[12%] hidden h-24 w-44 border border-gray-100 bg-white/60 md:block lg:right-[15%] lg:h-28 lg:w-56" />
						<div className="pointer-events-none absolute bottom-12 left-[29%] hidden h-24 w-44 border border-gray-100 bg-white/60 md:block lg:left-[30%] lg:h-28 lg:w-56" />

						{FLOATING_BADGES.map((badge) => (
							<span
								key={badge.key}
								className={`pointer-events-none absolute hidden rounded px-2 py-1 text-[10px] font-medium shadow-sm sm:inline-flex ${badge.className}`}
							>
								{t(badge.key)}
							</span>
						))}

						<div className="relative z-10 mx-auto flex min-h-[150px] max-w-[620px] flex-col items-center justify-center text-center sm:min-h-[190px] lg:min-h-[230px]">
							<h2 className="mb-5 text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
								{t("readyToStartTitle")}
							</h2>
							<Link
								href="/upload"
								className="inline-flex items-center justify-center gap-2 rounded-full bg-themePrimary400 px-6 py-3 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#83b751] hover:text-white hover:shadow-lg md:px-8 md:py-3.5 md:text-base"
							>
								{t("readyToStartGetQuote")}
								<ArrowRight className="h-4 w-4" weight="bold" aria-hidden="true" />
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
